import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getGuide } from '../../../../api/stays-guide-client';
import type { GuideBundle, GuideFaq, GuideMedia, GuideTip, StaffGuide } from '../../../../interfaces/stays-guide';
import type { StaysListingHead } from '../../../../interfaces/stays';
import { BackButton } from '../../../layout/back-button';
import { ConfirmDialog } from '../../../modal/confirm-dialog';
import { IndicatorGroup, StateSwitch } from '../../../../motion';
import { Tabs, TabIndicator, TabLabel } from '../../account/account.styled';
import { FormSkeleton, LoadError } from '../../dashboard/dashboard-shared-components/load-state';
import { GuideEditorTab, guideTabHref, guideTabOf, listingSectionHref } from '../listing/stays-listing-routes';
import { AccessDetailsForm } from './access-details-form';
import { FaqManager } from './faq-manager';
import { GalleryManager } from './gallery-manager';
import { GuideEditorForm } from './guide-editor-form';
import { Banner, BannerText, EditorTab, Muted, Page, TopBar } from './guide-editor.styled';
import { READ_ONLY_REASON } from './guide-model';
import { GuidePhotosEditor } from './guide-photos-editor';
import { GuidePreview } from './guide-preview';
import { LocalTipsEditor } from './local-tips-editor';

/**
 * THE HOST GUIDE EDITOR (plan S7.8) — the listing's Guide tab, at
 * /dashboard/stays/<id>/guide[/access|photos|faqs|gallery|tips|preview].
 *
 * The editor's tabs are ROUTE LINKS (the AccountPage pattern, "Guide editor"),
 * so every tab is an address a journey can open. The tab comes off the path
 * (the dashboard's splat — STAYS_LISTING_PATH group 4), never a param.
 *
 * ONE READ of the guide (GET …/guide: the words, FAQs, tips, photos, the
 * configured booleans) feeds every tab but two: Access details reads the
 * VALUES itself when it opens and drops them when it closes, and Preview reads
 * the preview endpoint. Without stays:manage the Access details tab is not
 * drawn, every write control is hidden or read-only, and the reason is shown
 * once.
 *
 * Leaving the Guide form with unsaved changes — another editor tab, the back
 * button, closing the page — asks first ("Leave without saving?").
 */

const TABS: ReadonlyArray<{ key: GuideEditorTab; label: string; manageOnly?: boolean }> = [
  { key: 'guide', label: 'Guide' },
  { key: 'access', label: 'Access details', manageOnly: true },
  { key: 'photos', label: 'Photos' },
  { key: 'faqs', label: 'FAQs' },
  { key: 'gallery', label: 'Gallery' },
  { key: 'tips', label: 'Local tips' },
  { key: 'preview', label: 'Preview' },
];

const DEFAULT_LIMITS = { faqs: 50, tips: 60, gallery: 40, checklist: 10 };

export interface GuideEditorShellProps {
  listing: StaysListingHead;
  canManage: boolean;
}

export const GuideEditorShell: React.FC<GuideEditorShellProps> = ({ listing, canManage }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const asked = guideTabOf(location.pathname);
  // Access details is stays:manage only: anyone else on its address sees the Guide form.
  const tab: GuideEditorTab = asked === 'access' && !canManage ? 'guide' : asked;

  const [view, setView] = useState<'loading' | 'error' | 'ready'>('loading');
  const [bundle, setBundle] = useState<GuideBundle | null>(null);
  const [dirty, setDirty] = useState(false);
  const [leaveTo, setLeaveTo] = useState<string | null>(null);
  const seq = useRef(0);

  const load = useCallback(
    async (quiet = false) => {
      const mine = ++seq.current;
      if (!quiet) setView('loading');
      try {
        const b = await getGuide(listing.id);
        if (mine !== seq.current) return;
        setBundle(b);
        setView('ready');
      } catch {
        if (mine !== seq.current) return;
        setView(cur => (quiet && cur === 'ready' ? 'ready' : 'error'));
      }
    },
    [listing.id]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  // The Guide form unmounts when another tab opens: nothing is dirty then.
  useEffect(() => {
    if (tab !== 'guide') setDirty(false);
  }, [tab]);

  // Closing or reloading the page with unsaved words asks the browser to ask.
  useEffect(() => {
    if (!dirty) return undefined;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);

    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty]);

  const go = (href: string) => {
    if (dirty && tab === 'guide') setLeaveTo(href);
    else navigate(href);
  };

  const onSaved = useCallback((guide: StaffGuide) => setBundle(b => (b ? { ...b, guide } : b)), []);
  const onMediaChange = useCallback(
    (update: (cur: GuideMedia[]) => GuideMedia[]) => setBundle(b => (b ? { ...b, media: update(b.media) } : b)),
    []
  );
  const onFaqsChange = useCallback(
    (update: (cur: GuideFaq[]) => GuideFaq[]) => setBundle(b => (b ? { ...b, faqs: update(b.faqs) } : b)),
    []
  );
  const onTipsChange = useCallback(
    (update: (cur: GuideTip[]) => GuideTip[]) => setBundle(b => (b ? { ...b, tips: update(b.tips) } : b)),
    []
  );
  const reload = useCallback(() => load(true), [load]);

  const visible = TABS.filter(t => canManage || !t.manageOnly);
  const limits = (bundle && bundle.limits) || DEFAULT_LIMITS;

  const body = (): React.ReactNode => {
    if (tab === 'preview') {
      return canManage ? (
        <GuidePreview listingId={listing.id} />
      ) : (
        <Muted role="status">The preview needs Stays management access.</Muted>
      );
    }
    if (!bundle) return null;
    switch (tab) {
      case 'access':
        return (
          <AccessDetailsForm
            listingId={listing.id}
            revealMinutes={bundle.guide.reveal_minutes}
            expiryMinutes={bundle.guide.expiry_minutes}
            onSaved={status => setBundle(b => (b ? { ...b, access_status: status } : b))}
          />
        );
      case 'photos':
        return <GuidePhotosEditor listingId={listing.id} media={bundle.media} canManage={canManage} onMediaChange={onMediaChange} />;
      case 'faqs':
        return (
          <FaqManager
            listingId={listing.id}
            faqs={bundle.faqs}
            limit={limits.faqs}
            canManage={canManage}
            onFaqsChange={onFaqsChange}
            onReload={reload}
          />
        );
      case 'gallery':
        return (
          <GalleryManager
            listingId={listing.id}
            media={bundle.media}
            limit={limits.gallery}
            canManage={canManage}
            onMediaChange={onMediaChange}
            onReload={reload}
          />
        );
      case 'tips':
        return (
          <LocalTipsEditor
            listingId={listing.id}
            tips={bundle.tips}
            media={bundle.media}
            limit={limits.tips}
            canManage={canManage}
            onTipsChange={onTipsChange}
            onMediaChange={onMediaChange}
            onReload={reload}
          />
        );
      case 'guide':
      default:
        return (
          <GuideEditorForm
            listingId={listing.id}
            bundle={bundle}
            canManage={canManage}
            onSaved={onSaved}
            onReload={reload}
            onDirtyChange={setDirty}
          />
        );
    }
  };

  return (
    <Page data-testid="guide-editor">
      <TopBar>
        <BackButton
          to={listingSectionHref(listing.id, 'overview')}
          label={`Back to ${listing.name}`}
          onClick={() => go(listingSectionHref(listing.id, 'overview'))}
        />
      </TopBar>

      <Tabs aria-label="Guide editor">
        <IndicatorGroup id="stays-guide-tabs">
          {visible.map(t => {
            const current = t.key === tab;
            const href = guideTabHref(listing.id, t.key);

            return (
              <EditorTab
                key={t.key}
                to={href}
                aria-current={current ? 'page' : undefined}
                onClick={e => {
                  if (current) return;
                  if (dirty && tab === 'guide') {
                    e.preventDefault();
                    setLeaveTo(href);
                  }
                }}
              >
                {current ? <TabIndicator id="stays-guide-tabs" data-active-indicator="true" /> : null}
                <TabLabel>{t.label}</TabLabel>
              </EditorTab>
            );
          })}
        </IndicatorGroup>
      </Tabs>

      {!canManage ? (
        <Banner $tone="neutral" role="status" data-testid="guide-read-only">
          <BannerText>{READ_ONLY_REASON}</BannerText>
        </Banner>
      ) : null}

      {tab === 'preview' ? (
        body()
      ) : (
        <StateSwitch state={view}>
          {view === 'loading' ? <FormSkeleton fields={5} label="guide" /> : null}
          {view === 'error' ? <LoadError message="Couldn't load the guide" onRetry={() => void load(false)} /> : null}
          {view === 'ready' ? body() : null}
        </StateSwitch>
      )}

      <ConfirmDialog
        open={!!leaveTo}
        title="Leave without saving?"
        message="Your changes to the guide are not saved yet."
        primaryLabel="Discard changes"
        primaryVariant="destructive"
        dismissLabel="Keep editing"
        onPrimary={() => {
          const to = leaveTo;
          setLeaveTo(null);
          setDirty(false);
          if (to) navigate(to);
        }}
        onDismiss={() => setLeaveTo(null)}
      />
    </Page>
  );
};
